import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Phone, MapPin, Clock, CheckCircle, XCircle, 
  Trash2, MessageSquare, ArrowUpRight, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  { value: 'contacted', label: 'Contacted', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  { value: 'quoted', label: 'Quoted', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  { value: 'converted', label: 'Converted', bg: 'bg-green-500/20', text: 'text-green-400' },
  { value: 'lost', label: 'Lost', bg: 'bg-red-500/20', text: 'text-red-400' },
];

const getStatusStyle = (status) => STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const loadLeads = useCallback(async () => {
    try {
      const res = await api.admin.getLeads();
      setLeads(res.data);
    } catch (e) {
      console.error('Failed to load leads:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await api.admin.updateLead(leadId, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await api.admin.deleteLead(leadId);
      setLeads(prev => prev.filter(l => l.id !== leadId));
      toast.success('Lead deleted');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const filtered = filter === 'all' ? leads : leads.filter(l => l.status === filter);
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    converted: leads.filter(l => l.status === 'converted').length,
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-pulse text-white/40">Loading leads...</div></div>;

  return (
    <div className="space-y-6" data-testid="leads-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-electric-teal" /> Leads
          </h2>
          <p className="text-white/50 text-sm mt-1">Form submissions from your SEO landing pages</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-secondary-dark rounded-lg p-4 border border-white/5 text-center">
          <p className="text-white font-bold text-2xl">{stats.total}</p>
          <p className="text-white/40 text-xs">Total</p>
        </div>
        <div className="bg-secondary-dark rounded-lg p-4 border border-blue-500/10 text-center">
          <p className="text-blue-400 font-bold text-2xl">{stats.new}</p>
          <p className="text-white/40 text-xs">New</p>
        </div>
        <div className="bg-secondary-dark rounded-lg p-4 border border-amber-500/10 text-center">
          <p className="text-amber-400 font-bold text-2xl">{stats.contacted}</p>
          <p className="text-white/40 text-xs">Contacted</p>
        </div>
        <div className="bg-secondary-dark rounded-lg p-4 border border-green-500/10 text-center">
          <p className="text-green-400 font-bold text-2xl">{stats.converted}</p>
          <p className="text-white/40 text-xs">Converted</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-white/30" />
        <div className="flex gap-1 flex-wrap">
          {[{ value: 'all', label: 'All' }, ...STATUS_OPTIONS].map(opt => (
            <button key={opt.value} onClick={() => setFilter(opt.value)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === opt.value ? 'bg-electric-teal/20 text-electric-teal border border-electric-teal/30' : 'bg-white/5 text-white/40 border border-white/10 hover:border-white/20'}`}
              data-testid={`filter-${opt.value}`}
            >
              {opt.label} {opt.value !== 'all' && `(${leads.filter(l => l.status === opt.value).length})`}
            </button>
          ))}
        </div>
      </div>

      {/* Leads List */}
      {filtered.length === 0 ? (
        <div className="bg-secondary-dark rounded-lg p-8 text-center border border-white/5">
          <Users className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-white/30 text-sm">{filter === 'all' ? 'No leads yet. They\'ll appear here when customers submit forms on your landing pages.' : `No ${filter} leads.`}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(lead => {
            const style = getStatusStyle(lead.status);
            return (
              <div key={lead.id} className="bg-secondary-dark rounded-lg border border-white/5 p-4 hover:border-white/10 transition-colors" data-testid={`lead-${lead.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-white font-semibold">{lead.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>{style.label}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-white/40 text-xs">
                      <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</span>
                      {lead.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {lead.city}</span>}
                      {lead.service && <span className="flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> {lead.service.replace(/-/g, ' ')}</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(lead.created_at).toLocaleDateString()}</span>
                    </div>
                    {lead.details && <p className="text-white/30 text-xs mt-1.5 flex items-start gap-1"><MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" /> {lead.details}</p>}
                    {lead.source_page && <p className="text-white/20 text-xs mt-0.5">Source: /{lead.source_page}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Select value={lead.status} onValueChange={(v) => handleStatusChange(lead.id, v)}>
                      <SelectTrigger className="bg-primary-dark border-white/10 text-white text-xs w-28 h-8" data-testid={`status-select-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button onClick={() => handleDelete(lead.id)} variant="ghost" size="sm" className="text-red-500/40 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsTab;
